// utils/pdfGenerator.js - Reusable PDF generation utility

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { riskBandFor } from './report/shapeReport';
import { generateEditorialPDF } from './report/editorialPdf';

/**
 * Load PDF libraries dynamically
 */
const loadPDFLibraries = async () => {
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  window.jspdf = window.jspdf || window.jsPDF;
};

/**
 * Generate a security report PDF from scan results
 * @param {Object} results - Scan results data
 * @param {string} domain - Domain name
 * @param {Object} options - PDF options
 * @param {string} options.theme - 'dark' or 'light'
 * @param {string} options.prospectName - Prospect name for personalization
 * @param {string} options.prospectCompany - Prospect company
 * @param {boolean} options.isCyberMode - Cyber theme mode
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<Blob|void>} PDF blob or saves directly
 */
// The report layout moved to report/editorialPdf.js — a print design rather than a dark
// web page photographed onto A4. Every caller keeps this entry point and its options, so
// nothing else had to change; `theme` is the one option that no longer applies, because
// the document has one intentional palette (overridable through `palette`).
//
// The implementation below is the previous generator. It is no longer reached and is kept
// only until the new layout has been through a release; delete it after that.
export const generateSecurityPDF = async (results, domain, options = {}) => {
  const { theme, ...rest } = options;
  return generateEditorialPDF(results, domain, rest);
};

// eslint-disable-next-line no-unused-vars
const generateSecurityPDFLegacy = async (results, domain, options = {}) => {
  const {
    theme = 'light',
    prospectName = '',
    prospectCompany = '',
    isCyberMode = false,
    onProgress = null,
    saveFile = true,
    fileName = null
  } = options;

  const isDarkMode = theme === 'dark';

  // What the report calls the thing it is about.
  //
  // `domain` arrives as whatever was typed into the scan form — scan rows store the raw
  // value, so "https://workservicescorp.com/" reached the 36px header verbatim and ran off
  // the card. The scanner itself normalizes before scanning, so the report was also the
  // only place showing a form of the target that no check ever used.
  //
  // When a company is known it becomes the heading and the domain drops to a subheading:
  // a salesperson with ten reports open needs to know whose it is, and the domain is the
  // slowest way to answer that.
  const displayDomain = String(domain ?? '')
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')   // scheme
    .replace(/\/.*$/, '')                       // path, query, fragment
    .replace(/\.+$/, '')                        // trailing dot
    || String(domain ?? '');
  const headline = prospectCompany || displayDomain;
  // Long targets are the common case for the company-less report, so the heading scales
  // with its own length rather than relying on a single size that fits the average one.
  const headlineSize = headline.length > 38 ? 22 : headline.length > 26 ? 27 : 36;

  // Report progress
  const reportProgress = (step, message) => {
    if (onProgress && typeof onProgress === 'function') {
      onProgress(step, message);
    }
  };

  reportProgress(0, 'Initializing PDF generation...');

  // ===== HELPER FUNCTIONS =====
  const getSeverityStats = (categories) => {
    const stats = { critical: 0, high: 0, medium: 0, low: 0 };
    categories?.forEach(cat => {
      cat.findings?.forEach(f => {
        const s = f.severity?.toLowerCase();
        if (stats[s] !== undefined) stats[s]++;
      });
    });
    return stats;
  };

  // Driven by the risk BAND, not the raw score. Deriving the label from the number alone
  // meant a low score always read "CRITICAL" even when the scan found no critical
  // findings — the contradiction users kept reporting.
  const RISK_STYLES = {
    low: {
      label: 'LOW',
      color: '#4b746fff',
      gradient: 'linear-gradient(135deg, #2ec4b6 0%, #6cdfd3 100%)',
      glow: '0 0 20px rgba(46, 196, 182, 0.5)'
    },
    medium: {
      label: 'MEDIUM',
      color: '#eab308',
      gradient: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)',
      glow: '0 0 20px rgba(234, 179, 8, 0.5)'
    },
    high: {
      label: 'HIGH',
      color: '#ff9f1c',
      gradient: 'linear-gradient(135deg, #ff9f1c 0%, #ffbf69 100%)',
      glow: '0 0 20px rgba(255, 159, 28, 0.5)'
    },
    critical: {
      label: 'CRITICAL',
      color: '#ff3b6f',
      gradient: 'linear-gradient(135deg, #ff3b6f 0%, #ff8e8e 100%)',
      glow: '0 0 20px rgba(255, 59, 111, 0.5)'
    }
  };
  const getRiskLevelWithColor = (band) => RISK_STYLES[band] || RISK_STYLES.low;

  const getBgGradient = () => {
    if (isCyberMode) {
      return 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)';
    }
    if (isDarkMode) {
      return 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)';
    }
    return 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
  };

  const getCardGradient = () => {
    if (isCyberMode) {
      return 'linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(123, 97, 255, 0.1) 100%)';
    }
    if (isDarkMode) {
      return 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)';
    }
    return 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)';
  };

  reportProgress(10, 'Processing scan results...');

  // ===== CALCULATE STATS =====
  // 1. Collect and De-duplicate findings
  const uniqueFindingsMap = new Map();
  results.categories?.forEach(cat => {
    cat.findings?.forEach(f => {
      const key = `${f.title?.trim()}-${f.description?.trim()}`.toLowerCase();
      if (!uniqueFindingsMap.has(key)) {
        let severity = f.severity?.toLowerCase() || 'low';
        if (!['critical', 'high', 'medium', 'low'].includes(severity)) {
          severity = 'low';
        }
        uniqueFindingsMap.set(key, {
          ...f,
          severity,
          category: cat.name,
          icon: cat.icon
        });
      }
    });
  });
  const allFindings = Array.from(uniqueFindingsMap.values());

  // 2. Sort findings
  const severityPriority = { critical: 0, high: 1, medium: 2, low: 3 };
  allFindings.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return (severityPriority[a.severity] ?? 99) - (severityPriority[b.severity] ?? 99);
  });

  // 3. Calculate stats
  const stats = { critical: 0, high: 0, medium: 0, low: 0 };
  allFindings.forEach(f => {
    stats[f.severity]++;
  });

  const totalFindings = allFindings.length;
  const criticalCount = stats.critical;
  const highCount = stats.high;

  // 4. Normalize categories
  const normalizedCategories = results.categories?.map(cat => {
    const catFindings = allFindings.filter(f => f.category === cat.name);
    return { ...cat, findings: catFindings };
  }).filter(cat => cat.findings.length > 0);

  // 5. Security Score — taken from the backend, never recomputed here.
  // This used to average severity points across EVERY finding including passes
  // ((critical*0 + high*25 + medium*50 + low*100) / total) and overwrite results.riskScore,
  // so the PDF a prospect received disagreed with the score shown in the app, and adding
  // passing checks raised the score. The backend's subtractive model is authoritative:
  // it starts at 100 and deducts per non-passing finding. 100 = risk free, 0 = critical.
  const calculatedRiskScore = Number.isFinite(results.riskScore) ? results.riskScore : 100;
  const risk = getRiskLevelWithColor(riskBandFor(results, calculatedRiskScore));

  reportProgress(20, 'Building PDF content...');

  // ===== BUILD HTML CONTENT =====
  const buildExecutiveSummary = () => {
    const mostSevere = Object.entries(stats)
      .sort((a, b) => b[1] - a[1])[0] || ['low', 0];

    const criticalPct = totalFindings
      ? Math.round(((stats.critical + stats.high) / totalFindings) * 100)
      : 0;

    return `
    <div style="
      margin-top: 28px;
      padding: 28px;
      border-radius: 24px;
      background: ${getCardGradient()};
      border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
      backdrop-filter: blur(10px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.1);
      position: relative;
      overflow: hidden;
    ">
      <div style="
        position: absolute;
        top: 0;
        right: 0;
        width: 200px;
        height: 200px;
        background: radial-gradient(circle, ${risk.color}15 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(50px, -50px);
      "></div>
      
      <div style="position: relative; z-index: 1;">
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
          <div style="
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: ${risk.color};
            box-shadow: ${risk.glow};
          "></div>
          <span style="
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: ${risk.color};
          ">Executive Analysis</span>
        </div>
        
        <h3 style="
          margin: 0 0 16px;
          font-size: 28px;
          color:${isDarkMode ? '#0f172a' : '#f1f5f9'};
          font-weight: 700;
          letter-spacing: -0.5px;
          background: linear-gradient(135deg, ${isDarkMode ? '#fff' : '#0f172a'}, ${risk.color});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        ">
          Security Posture Assessment
        </h3>
        
        <p style="
          font-size: 15px;
          line-height: 1.7;
          color: ${isDarkMode ? '#e2e8f0' : '#334155'};
          margin-bottom: 20px;
        ">
          <span style="
            display: inline-block;
            padding: 4px 12px;
            border-radius: 100px;
            background: ${risk.color}20;
            color: ${risk.color};
            font-weight: 700;
            margin-right: 8px;
            overflow-wrap: anywhere;
          ">${displayDomain}</span>
          exhibits a <strong style="color: ${risk.color};">${risk.label}</strong> risk profile
          with a security score of <strong style="color: ${risk.color};">${results.riskScore || 50}/100</strong>,
          on a scale where <strong>100 is the best possible result</strong> and 0 is the worst.
        </p>

        <div style="
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-top: 24px;
        ">
          <div style="
            padding: 16px;
            border-radius: 16px;
            background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
            border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
          ">
            <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
              ${criticalPct}%
            </div>
            <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
              High-Impact Issues
            </div>
          </div>
          <div style="
            padding: 16px;
            border-radius: 16px;
            background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
            border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
          ">
            <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
              ${mostSevere[1]}
            </div>
            <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
              ${mostSevere[0].toUpperCase()} Severity Issues
            </div>
          </div>
          <div style="
            padding: 16px;
            border-radius: 16px;
            background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
            border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
          ">
            <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
              ${stats.critical}
            </div>
            <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
              Critical Vulnerabilities
            </div>
          </div>
        </div>

        <div style="
          margin-top: 24px;
          padding: 20px;
          border-radius: 16px;
          background: ${isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)'};
          border-left: 4px solid ${risk.color};
        ">
          <div style="display: flex; gap: 12px; align-items: flex-start;">
            <div style="font-size: 20px;">⚡</div>
            <div>
              <div style="font-weight: 700; margin-bottom: 4px; color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};">
                ${stats.critical ? 'Immediate Action Required' : 'Proactive Security Posture'}
              </div>
              <div style="font-size: 13px; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                ${stats.critical
          ? `${stats.critical} critical vulnerabilities require immediate remediation to prevent potential breaches.`
          : `No critical vulnerabilities detected. Continue maintaining strong security practices.`
        }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
  };

  const infoCard = (label, value, icon) => `
  <div style="
    padding: 20px;
    border-radius: 20px;
    background: ${getCardGradient()};
    border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  ">
    <div style="
      position: absolute;
      top: 0;
      right: 0;
      width: 100px;
      height: 100px;
      background: radial-gradient(circle, ${isDarkMode ? '#8b5cf620' : '#8b5cf610'} 0%, transparent 70%);
      border-radius: 50%;
      transform: translate(30px, -30px);
    "></div>
    <div style="position: relative; z-index: 1;">
      <div style="font-size: 24px; margin-bottom: 8px;">${icon}</div>
      <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'}; margin-bottom: 4px;">
        ${label}
      </div>
      <div style="font-size: 16px; font-weight: 700; color: ${isDarkMode ? '#f1f5f9' : '#0f172a'}; word-break: break-word;">
        ${value}
      </div>
    </div>
  </div>
`;

  const severityBadge = (severity, count) => {
    const colors = {
      critical: { bg: '#ff3b6f20', color: '#ff3b6f', icon: '🔴' },
      high: { bg: '#ff9f1c20', color: '#ff9f1c', icon: '🟠' },
      medium: { bg: '#eab30820', color: '#eab308', icon: '🟡' },
      low: { bg: '#2ec4b620', color: '#2ec4b6', icon: '🔵' }
    };
    const c = colors[severity] || colors.low;

    return `
    <div style="
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      border-radius: 100px;
      background: ${c.bg};
      border: 1px solid ${c.color}30;
    ">
      <span style="font-size: 14px;">${c.icon}</span>
      <span style="font-size: 13px; font-weight: 600; color: ${c.color};">${count}</span>
      <span style="font-size: 12px; opacity: 0.8; color: ${isDarkMode ? '#e2e8f0' : '#334155'};">${severity}</span>
    </div>
  `;
  };

  const buildRow = (cat) => {
    const catStats = { critical: 0, high: 0, medium: 0, low: 0 };
    cat.findings?.forEach(f => {
      const s = f.severity?.toLowerCase();
      if (catStats[s] !== undefined) catStats[s]++;
    });
    const total = Object.values(catStats).reduce((a, b) => a + b, 0);

    return `
    <tr style="
      background: ${isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)'};
      border-bottom: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
      transition: all 0.2s ease;
    ">
      <td style="
        padding: 16px 12px;
        font-weight: 600;
        color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
        border-radius: 12px 0 0 12px;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 20px;">${cat.icon || '🔍'}</span>
          <span>${cat.name}</span>
        </div>
      </td>
      <td align="center" style="padding: 16px 12px;">
        <span style="
          display: inline-block;
          padding: 4px 10px;
          border-radius: 100px;
          background: ${catStats.critical ? '#ff3b6f20' : 'transparent'};
          color: ${catStats.critical ? '#ff3b6f' : '#9ca3af'};
          font-weight: ${catStats.critical ? '700' : '400'};
        ">${catStats.critical || '0'}</span>
      </td>
      <td align="center" style="padding: 16px 12px;">
        <span style="
          display: inline-block;
          padding: 4px 10px;
          border-radius: 100px;
          background: ${catStats.high ? '#ff9f1c20' : 'transparent'};
          color: ${catStats.high ? '#ff9f1c' : '#9ca3af'};
          font-weight: ${catStats.high ? '700' : '400'};
        ">${catStats.high || '0'}</span>
      </td>
      <td align="center" style="padding: 16px 12px;">
        <span style="
          display: inline-block;
          padding: 4px 10px;
          border-radius: 100px;
          background: ${catStats.medium ? '#eab30820' : 'transparent'};
          color: ${catStats.medium ? '#eab308' : '#9ca3af'};
          font-weight: ${catStats.medium ? '700' : '400'};
        ">${catStats.medium || '0'}</span>
      </td>
      <td align="center" style="padding: 16px 12px;">
        <span style="
          display: inline-block;
          padding: 4px 10px;
          border-radius: 100px;
          background: ${catStats.low ? '#2ec4b620' : 'transparent'};
          color: ${catStats.low ? '#2ec4b6' : '#9ca3af'};
          font-weight: ${catStats.low ? '700' : '400'};
        ">${catStats.low || '0'}</span>
      </td>
      <td align="center" style="
        padding: 16px 12px;
        font-weight: 800;
        color: ${isDarkMode ? '#a78bfa' : '#8b5cf6'};
        border-radius: 0 12px 12px 0;
      ">
        <span style="
          display: inline-block;
          padding: 4px 12px;
          background: ${isDarkMode ? '#8b5cf620' : '#8b5cf610'};
          border-radius: 100px;
        ">${total}</span>
      </td>
    </tr>
  `;
  };

  reportProgress(30, 'Creating paginated content...');

  // ===== CREATE PAGINATED CONTENT =====
  const createPaginatedContent = () => {
    const pages = [];

    // PAGE 1 - Cover and Summary
    let page1 = `
    <div style="
      color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      position: relative;
    ">
      <!-- Futuristic Header -->
      <div style="
        position: relative;
        padding: 32px 32px 28px;
        border-radius: 32px;
        background: ${isCyberMode
        ? 'linear-gradient(135deg, #00ff8820 0%, #7b61ff20 100%)'
        : `linear-gradient(135deg, ${isDarkMode ? '#1e293b' : '#8b5cf6'}, ${isDarkMode ? '#0f172a' : '#7c3aed'})`
      };
        color: white;
        margin-bottom: 28px;
        overflow: hidden;
        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
      ">
        <div style="
          position: absolute;
          top: -50px;
          right: -50px;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%);
          border-radius: 50%;
        "></div>
        <div style="
          position: absolute;
          bottom: -50px;
          left: -50px;
          width: 150px;
          height: 150px;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          border-radius: 50%;
        "></div>
        
        <div style="position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: center;">
          <div style="display:flex;align-items:center;gap:14px">
        <div>
              <div style="
                font-size: 14px;
                font-weight: 600;
                letter-spacing: 3px;
                text-transform: uppercase;
                opacity: 0.9;
                margin-bottom: 8px;
              ">${prospectName || prospectCompany ? 'PROSPECT SECURITY REPORT' : 'SECURITY REPORT'}</div>
              <h1 style="
                margin: 0;
                font-size: ${headlineSize}px;
                font-weight: 800;
                letter-spacing: -1px;
                line-height: 1.15;
                max-width: 560px;
                overflow-wrap: anywhere;
              ">
                ${headline}
              </h1>
              ${prospectCompany ? `
                <p style="margin-top: 6px; font-size: 15px; font-weight: 600; opacity: 0.85; overflow-wrap: anywhere;">
                  ${displayDomain}
                </p>
              ` : ''}
              ${prospectName ? `
                <p style="margin-top: 6px; font-size: 14px; opacity: 0.8;">
                  Prepared for: ${prospectName}
                </p>
              ` : ''}
              <p style="margin-top: 4px; font-size: 15px; opacity: 0.9; display: flex; align-items: center; gap: 8px;">
                <span>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span style="display: inline-block; width: 4px; height: 4px; background: white; border-radius: 50%; opacity: 0.5;"></span>
              </p>
            </div>
          </div>
          
          <div style="
            text-align: right;
            background: rgba(255,255,255,0.1);
            padding: 20px;
            border-radius: 24px;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.2);
          ">
            <div style="font-size: 56px; font-weight: 900; line-height: 1; letter-spacing: -2px;">
              ${results.riskScore}<span style="font-size: 20px; opacity: 0.8;">/100</span>
            </div>
            <div style="
              margin-top: 8px;
              padding: 6px 16px;
              border-radius: 100px;
              background: ${risk.color}30;
              color: white;
              font-size: 14px;
              font-weight: 700;
              letter-spacing: 1px;
            ">
              ${risk.label} RISK
            </div>
            <!-- Readers consistently assumed a lower number was better, the way a
                 vulnerability count or a CVSS rating works. The scale has to say which
                 way it runs on the page where the number first appears. -->
            <div style="margin-top: 10px; font-size: 11px; line-height: 1.4; opacity: 0.85; max-width: 190px;">
              Higher is better.<br />100 = no issues found, 0 = critical risk.
            </div>
          </div>
        </div>
      </div>

      <!-- Info Grid -->
      <div style="
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        margin-bottom: 24px;
      ">
        ${infoCard('Target', displayDomain, '🌐')}
        ${infoCard('Scan Date', new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), '📅')}
        ${infoCard('Total Findings', totalFindings.toString(), '⚠️')}
        ${infoCard('Risk Level', risk.label, risk.label === 'CRITICAL' ? '🔴' : risk.label === 'ELEVATED' ? '🟠' : '🟢')}
      </div>

      <!-- Executive Summary -->
      ${buildExecutiveSummary()}

      <!-- Severity Distribution -->
      <div style="
        margin-top: 28px;
        padding: 28px;
        border-radius: 24px;
        background: ${getCardGradient()};
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
        backdrop-filter: blur(10px);
        position: relative;
        overflow: hidden;
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #ff3b6f, #ff9f1c, #eab308, #2ec4b6);
        "></div>
        
        <h3 style="
          margin: 0 0 24px;
          font-size: 20px;
          font-weight: 700;
          display: flex;
          align-items: center;
          color: ${isDarkMode ? '#ffffff' : '#171718'};
          gap: 12px;
        ">
          <span style="
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #8b5cf6;
            box-shadow: 0 0 20px #8b5cf6;
          "></span>
          Severity Distribution
        </h3>
        
        <div style="display: flex; justify-content: space-around; align-items: center;">
          ${severityBadge('critical', stats.critical)}
          ${severityBadge('high', stats.high)}
          ${severityBadge('medium', stats.medium)}
          ${severityBadge('low', stats.low)}
        </div>

      </div>
    </div>
  `;

    pages.push(page1);

    reportProgress(40, 'Building categories page...');

    // PAGE 2 - Categories Summary
    let page2 = `
    <div style="
      color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      page-break-before: always;
      position: relative;
    ">
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-bottom: 28px;
        padding-bottom: 16px;
        border-bottom: 2px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
      ">
        <div>
          <h2 style="
            margin: 0 0 8px;
            font-size: 28px;
            font-weight: 800;
            letter-spacing: -0.5px;
            color: ${isDarkMode ? '#ffffff' : '#171718'};
            margin:5px;
            padding:0 20px
            width: 100%;
          ">
            Security Categories
          </h2>
          <p style="margin: 0; font-size: 14px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
            Detailed analysis by security domain
          </p>
        </div>
        <div style="
          padding: 8px 16px;
          background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
          border-radius: 100px;
          font-size: 13px;
          font-weight: 600;
        ">
          ${normalizedCategories?.length || 0} Categories · ${totalFindings} Total Findings
        </div>
      </div>

      <!-- Categories Table -->
      <div style="
        border-radius: 24px;
        overflow: hidden;
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
        margin-bottom: 28px;
      ">
        <table style="
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        ">
          <thead>
            <tr style="
              background: ${isDarkMode ? 'rgba(139, 92, 246, 0.2)' : '#f1f5f9'};
              color: ${isDarkMode ? '#e2e8f0' : '#475569'};
              font-weight: 700;
              text-transform: uppercase;
              font-size: 11px;
              letter-spacing: 1px;
            ">
              <th style="padding: 16px 12px; text-align: left;">Category</th>
              <th style="padding: 16px 12px; text-align: center;">Critical</th>
              <th style="padding: 16px 12px; text-align: center;">High</th>
              <th style="padding: 16px 12px; text-align: center;">Medium</th>
              <th style="padding: 16px 12px; text-align: center;">Low</th>
              <th style="padding: 16px 12px; text-align: center;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${normalizedCategories?.map(buildRow).join('')}
          </tbody>
        </table>
      </div>
      <div id="page2-findings-area"></div>
  `;

    const MAX_PAGE_HEIGHT = 1000;
    const measureHeight = (html) => {
      const testDiv = document.createElement('div');
      testDiv.style.width = '794px';
      testDiv.style.padding = '40px';
      testDiv.style.boxSizing = 'border-box';
      testDiv.style.visibility = 'hidden';
      testDiv.style.position = 'absolute';
      testDiv.style.fontFamily = 'Inter, sans-serif';
      testDiv.innerHTML = html;
      document.body.appendChild(testDiv);
      const height = testDiv.offsetHeight;
      document.body.removeChild(testDiv);
      return height;
    };

    let lastCategory = null;
    const buildCategoryHeader = (category, isContinuation = false) => `
    <h3 style="
      margin: 32px 0 20px;
      color: ${isDarkMode ? '#ffffff' : '#171718'};
      font-size: 22px;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 12px;
    ">
      <span style="
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #8b5cf6;
        box-shadow: 0 0 20px #8b5cf6;
      "></span>
      ${category}${isContinuation ? ' <span style="font-size: 14px; opacity: 0.5; font-weight: 400;">(Continued)</span>' : ''}
    </h3>
    <p style="margin: -15px 0 20px 20px; font-size: 14px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
      ${isContinuation ? 'Continuation of security findings' : 'Analysis of findings in this category'}
    </p>
  `;

    const renderFindingCard = (finding) => {
      const severityColor = finding.severity === 'critical' ? '#ef4444' :
        finding.severity === 'high' ? '#f97316' :
          finding.severity === 'medium' ? '#eab308' : '#3b82f6';
      const severityIcon = finding.severity === 'critical' ? '🔴' :
        finding.severity === 'high' ? '🟠' :
          finding.severity === 'medium' ? '🟡' : '🔵';

      return `
      <div style="
        margin-bottom: 16px;
        padding: 20px;
        border-radius: 20px;
        background: ${getCardGradient()};
        border: 1px solid ${severityColor}40;
        position: relative;
        overflow: hidden;
        width: 100%;
        box-sizing: border-box;
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: ${severityColor};
        "></div>
        <div style="display: flex; gap: 16px; margin-left: 8px;">
          <div style="font-size: 24px;">${severityIcon}</div>
          <div style="flex: 1;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <span style="
                font-weight: 700;
                font-size: 16px;
                color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
              ">${finding.title}</span>
               <div style="display: flex; gap: 8px; align-items: center;">
                <span style="
                  padding: 4px 12px;
                  border-radius: 100px;
                  background: ${severityColor}20;
                  color: ${severityColor};
                  font-size: 11px;
                  font-weight: 700;
                  text-transform: uppercase;
                  letter-spacing: 1px;
                ">${finding.severity}</span>
                ${(finding.severity !== 'low' && finding.severity !== 'info') ? `
                <span style="
                  padding: 4px 10px;
                  border-radius: 100px;
                  background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
                  color: ${isDarkMode ? '#94a3b8' : '#64748b'};
                  font-size: 10px;
                  font-weight: 700;
                  border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
                ">IMPACT: ${finding.impact || (finding.severity === 'critical' ? '25' : finding.severity === 'high' ? '15' : '10')} PTS</span>
                ` : ''}
              </div>
            </div>
            <div style="
              font-size: 13px;
              color: ${isDarkMode ? '#94a3b8' : '#64748b'};
              line-height: 1.6;
            ">${finding.description}</div>
            <div style="
              margin-top: 12px;
              font-size: 12px;
              color: ${isDarkMode ? '#a78bfa' : '#8b5cf6'};
              display: flex;
              align-items: center;
              gap: 6px;
            ">
              <span>${finding.icon || '🔍'}</span>
              <span>${finding.category}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    };

    reportProgress(50, 'Processing findings...');

    let page2FindingsHtml = '';
    let currentFindingsIndex = 0;

    for (let i = 0; i < allFindings.length; i++) {
      const finding = allFindings[i];
      let prospectiveHtml = page2FindingsHtml;
      let headerAdded = false;

      if (finding.category !== lastCategory) {
        prospectiveHtml += buildCategoryHeader(finding.category);
        headerAdded = true;
      }
      prospectiveHtml += renderFindingCard(finding);

      const fullPage2Height = measureHeight(page2 + prospectiveHtml);

      if (fullPage2Height > MAX_PAGE_HEIGHT) {
        break;
      } else {
        if (headerAdded) lastCategory = finding.category;
        page2FindingsHtml = prospectiveHtml;
        currentFindingsIndex = i + 1;
      }
    }

    pages.push(page2 + page2FindingsHtml);

    while (currentFindingsIndex < allFindings.length) {
      let currentPageHtml = '';
      let pageLastCategory = null;
      let itemsOnPage = 0;

      while (currentFindingsIndex < allFindings.length) {
        const finding = allFindings[currentFindingsIndex];
        let prospectiveHtml = currentPageHtml;
        let headerAdded = false;
        let isContinuation = false;

        if (finding.category !== pageLastCategory) {
          isContinuation = finding.category === lastCategory;
          prospectiveHtml += buildCategoryHeader(finding.category, isContinuation);
          headerAdded = true;
        }

        prospectiveHtml += renderFindingCard(finding);
        const h = measureHeight(prospectiveHtml);

        if (h > MAX_PAGE_HEIGHT && itemsOnPage > 0) {
          break;
        } else {
          if (headerAdded) {
            lastCategory = finding.category;
            pageLastCategory = finding.category;
          }
          currentPageHtml = prospectiveHtml;
          currentFindingsIndex++;
          itemsOnPage++;
        }
      }

      pages.push(`
      <div class="page" style="
        color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        padding: 40px;
        width: 794px;
        box-sizing: border-box;
      ">
        <div style="width: 100%;">
          ${currentPageHtml}
        </div>
      </div>
    `);
    }

    reportProgress(70, 'Rendering PDF pages...');
    return pages;
  };

  // ===== GENERATE PDF =====
  try {
    // Load PDF libraries
    await loadPDFLibraries();

    // Create temporary container
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '794px';
    tempContainer.style.padding = '32px';
    tempContainer.style.background = getBgGradient();
    tempContainer.style.fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, sans-serif';
    document.body.appendChild(tempContainer);

    // Generate paginated content
    const pages = createPaginatedContent();

    // Render each page
    const canvases = [];
    for (let i = 0; i < pages.length; i++) {
      tempContainer.innerHTML = pages[i];

      // Add spacing at bottom to prevent footer overlap
      const spacer = document.createElement('div');
      spacer.style.height = '40px';
      tempContainer.appendChild(spacer);

      const canvas = await html2canvas(tempContainer, {
        scale: 2.5,
        useCORS: true,
        allowTaint: false,
        backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
        logging: false,
        windowWidth: 794,
        onclone: (clonedDoc) => {
          const style = clonedDoc.createElement('style');
          style.textContent = `
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
            * { font-family: 'Inter', sans-serif !important; }
          `;
          clonedDoc.head.appendChild(style);
        }
      });

      canvases.push(canvas);
      reportProgress(70 + (i / pages.length) * 20, `Rendering page ${i + 1} of ${pages.length}...`);
    }

    document.body.removeChild(tempContainer);

    reportProgress(90, 'Creating PDF file...');

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      hotfixes: ['px_scaling']
    });

    const margin = 15;
    const pageWidth = 210;
    const contentWidth = pageWidth - margin * 2;

    for (let i = 0; i < canvases.length; i++) {
      const canvas = canvases[i];
      const scale = contentWidth / canvas.width;

      if (i > 0) pdf.addPage();
      
      // Fill full page background
      pdf.setFillColor(isDarkMode ? '#0f172a' : '#f8fafc');
      pdf.rect(0, 0, 210, 297, 'F');

      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        margin,
        margin,
        contentWidth,
        canvas.height * scale,
        undefined,
        'FAST'
      );

      // Footer
      pdf.setDrawColor(139, 92, 246);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 297 - 22, pageWidth - margin, 297 - 22);

      pdf.setFontSize(8);
      pdf.setTextColor(isDarkMode ? '#94a3b8' : '#64748b');
      pdf.setFont('helvetica', 'normal');

      const bottom = `Page ${i + 1} of ${canvases.length} · ${new Date().toLocaleDateString()}`;
      const footerY = 297 - 13;
      
      pdf.text('Confidential Security Report', margin, footerY);
      pdf.text(bottom, pageWidth / 2, footerY, { align: 'center' });
      
      if (prospectName) {
        pdf.text(`Prepared for: ${prospectName}`, pageWidth - margin, footerY, { align: 'right' });
      } else {
        pdf.text('Generated by Security Scanner', pageWidth - margin, footerY, { align: 'right' });
      }
    }

    reportProgress(100, 'PDF generation complete!');

    if (saveFile) {
      // Save the PDF
      const timestamp = new Date().toISOString().split('T')[0];
      const sanitizedDomain = domain.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const pdfFileName = fileName || `security-scan-${sanitizedDomain}-${timestamp}.pdf`;
      pdf.save(pdfFileName);
      return null;
    } else {
      // Return as blob
      return pdf.output('blob');
    }

  } catch (error) {
    console.error('PDF generation failed:', error);
    throw error;
  }
};

export default generateSecurityPDF;